import apiConfig from "../ApiConfig";

/**
 * Get dashboard statistics
 */
export const getDashboardStatsApi = async (customerId) => {
  try {
    const url = customerId
      ? `/api/trust-center/dashboard/stats?customer_id=${customerId}`
      : '/api/trust-center/dashboard/stats';
    const response = await apiConfig.get(url);
    return response.data;
  } catch (error) {
    console.error('Failed to load dashboard stats:', error);
    throw error;
  }
};

/**
 * Get framework types, categories, and countries
 */
export const getFrameworkTypesApi = async () => {
  try {
    const response = await apiConfig.get('/api/trust-center/framework-types');
    return response.data;
  } catch (error) {
    console.error('Failed to load framework types:', error);
    throw error;
  }
};

export const getTrustCenterFrameworksRoleBasedApi = async (
  page,
  pageSize,
  query
) => {
  const res = await apiConfig.get(
    `/api/trust-center/frameworks/role-based/?page=${page}&page_size=${pageSize}&${query}`
  );
  return res.data;
};

export const getTrustCenterFrameworksCustomerAdminApi = async (
  page,
  pageSize,
  customer_id,
  query
) => {
  const res = await apiConfig.get(
    `/api/trust-center/frameworks/customer-admin/?page=${page}&page_size=${pageSize}&customer_id=${customer_id}&${query}`
  );
  return res.data;
};
export const getcustomerAdminFrameworksApi = async (customer_id) => {
  const res = await apiConfig.get(
    `/api/trust-center/frameworks/customer-admin/?customer_id=${customer_id}`
  );
  return res.data;
}
export const getTrustCenterFrameworksEmployeeApi = async (employeeId) => {
  const res = await apiConfig.get(`/api/customer-framework/employee-frameworks/`);
  const frameworks = Array.isArray(res?.data?.framework) ? res.data.framework : [];
  return {
    ...res.data,
    results: frameworks,
    count: res?.data?.len ?? frameworks.length,
    total_pages: 1,
  };
};

export const getCustomerFrameworkListApi = async (customerId) => {
  const res = await apiConfig.get(
    `/api/customer-framework/get-customer-framework/${customerId}/`
  );
  return res.data;
};
export const getCustomerFrameworkDocumentApi = async (page, pageSize, filters = {}) => {
  const params = new URLSearchParams({ page, page_size: pageSize });
  if (filters.search) params.set('search', filters.search);
  if (filters.framework_id) params.set('framework_id', filters.framework_id);
  if (filters.category) params.set('category', filters.category);
  if (filters.customer_id) params.set('customer_id', filters.customer_id);
  if (filters.status) params.set('status', filters.status);
  const res = await apiConfig.get(`/api/customer-framework/get-customer-documents/?${params}`);
  return res.data;
};
export const getCustomerMyLibraryApi = async (page, pageSize, filters = {}) => {
  const params = new URLSearchParams({ page, page_size: pageSize });
  if (filters.search) params.set('search', filters.search);
  if (filters.framework_id) params.set('framework_id', filters.framework_id);
  if (filters.category) params.set('category', filters.category);
  if (filters.customer_id) params.set('customer_id', filters.customer_id);
  const res = await apiConfig.get(`/api/customer-framework/get-customer-library/?${params}`);
  return res.data;
};

export const getEndUserFilesApi = async (page, pageSize, filters = {}) => {
   const params = new URLSearchParams({ page, page_size: pageSize });
  if (filters.search) params.set('search', filters.search);
  if (filters.framework_id) params.set('framework_id', filters.framework_id);
  if (filters.category) params.set('category', filters.category);
  if (filters.customer_id) params.set('customer_id', filters.customer_id);
  const res = await apiConfig.get(`/api/customer-framework/end-user-files/?${params}`);
  return res.data;
};

export const getCustomizedFileUrlApi = async (payload) => {
  const res = await apiConfig.post(`/api/customer-framework/customized-file/url/`, payload);
  return res.data;
} 
/**
 * Create a new framework
 * @param {Object} frameworkData - Framework data (name, type, country, description)
 */
export const createFrameworkApi = async (frameworkData) => {
  try {
    const response = await apiConfig.post('/api/trust-center/frameworks', frameworkData);
    return response.data;
  } catch (error) {
    console.error('Failed to create framework:', error);
    throw error;
  }
};

/**
 * Get controls for a specific framework
 * @param {string} frameworkId - Framework ID
 * @param {number} page - Page number
 * @param {number} pageSize - Page size
 * @param {string} search - Search term
 */

export const getControlsApi = async (
  frameworkId,
  page = 1,
  pageSize = 20,
  filters = {}
) => {
  const params = new URLSearchParams({
    page: pageSize === "all" ? 1 : page,
    page_size: pageSize
  });

  // 🔥 append filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });

  const response = await apiConfig.get(
    `/api/sub-controls/${frameworkId}/?${params.toString()}`
  );

  return response.data;
};

export const getEmployeeFrameworkControlsApi = async (
  frameworkId,
  page = 1,
  pageSize = 20,
  filters = {}
) => {
  const params = new URLSearchParams({
    page,
    page_size: pageSize,
  });

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });

  const response = await apiConfig.get(
    `/api/customer-framework/employee-controls/${frameworkId}/?${params.toString()}`
  );

  return response.data;
};

/**
 * Create a new control
 * @param {Object} controlData - Control data
 */
export const createControlApi = async (controlData) => {
  try {
    const response = await apiConfig.post('/api/trust-center/controls', controlData);
    return response.data;
  } catch (error) {
    console.error('Failed to create control:', error);
    throw error;
  }
};


/**
 * Export trust center data
 * @param {string} format - Export format ('csv' or 'excel')
 * @param {string} token - Auth token
 */
export const exportDataApi = async (format = 'csv', token) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/trust-center/export?format=${format}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return await response.blob();
  } catch (error) {
    console.error('Failed to export data:', error);
    throw error;
  }
};

/**
 * Delete a framework
 * @param {string} frameworkId - Framework ID
 */
export const deleteFrameworkApi = async (frameworkId) => {
  try {
    const response = await apiConfig.delete(`/api/trust-center/frameworks/${frameworkId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete framework:', error);
    throw error;
  }
};

/**
 * Toggle framework status (Active/Inactive)
 * @param {string} frameworkId - Framework ID
 * @param {string} status - New status ('Active' or 'Inactive')
 */
export const toggleFrameworkStatusApi = async (frameworkId, status) => {
  try {
    const response = await apiConfig.patch(`/api/trust-center/frameworks/${frameworkId}/toggle-status`, { status });
    return response.data;
  } catch (error) {
    console.error('Failed to toggle framework status:', error);
    throw error;
  }
};



export const downloadControlFileApi = async (responseId) => {
  const res = await apiConfig.get(
    `/api/trust-center/files/${responseId}/`
  );
  return res.data;
};
/**
 * Fetch enabled TrustCenter frameworks for a customer (from customer-requirements)
 */
export const fetchTrustCenterEnabledFrameworksApi = async ({ customerId }) => {
  try {
    // const tokenLocal = localStorage.getItem("token");

    // Determine correct tenant/customer ID
    // const customerId =
    //     tenantId ||
    //     user?.tenant_id ||
    //     localStorage.getItem("tenantId") ||
    //     user?.customer_id ||
    //     user?.id;

    // if (!customerId) {
    //     console.warn("⚠ No tenant/customer ID found for TrustCenter API");
    //     return { enabledFrameworks: [], rawPayload: {}, success: false };
    // }
    console.log({ customerIdformapi: customerId });
    const url = `/api/customer-requirements/${customerId}/?type=TrustCenter`;

    const response = await apiConfig.get(url)

    let payload = response.data?.data ?? response.data ?? {};

    // Normalize possible variations
    if (payload?.type?.TrustCenter) payload = payload.type.TrustCenter;
    if (payload?.TrustCenter) payload = payload.TrustCenter;
    if (payload?.Default && payload?.TrustCenter) payload = payload.TrustCenter;
    if (Array.isArray(payload)) payload = {};

    if (!payload || typeof payload !== "object") {
      console.warn("Unexpected TrustCenter payload:", payload);
      return { enabledFrameworks: [], rawPayload: {}, success: true };
    }

    const enabledFrameworks = Object.entries(payload)
      .filter(([_, enabled]) => enabled === true)
      .map(([name]) => String(name).trim());

    return {
      enabledFrameworks,
      rawPayload: payload,
      success: true,
    };
  } catch (error) {
    console.error("❌ Error fetching TrustCenter enabled frameworks:", error);
    return { enabledFrameworks: [], rawPayload: {}, success: false };
  }
};


export const fetchAllFrameworksApi = async () => {
  try {
    const response = await apiConfig.get('/api/trust-center/frameworks');

    const data = response.data;

    // Normalizing grouped or flat data
    const frameworks = Array.isArray(data)
      ? data
      : Object.values(data).flat();

    return frameworks;
  } catch (error) {
    console.error("Failed to load TrustCenter frameworks:", error);
    throw error;
  }
};

export const fetchTrustCenterDashboardApi = async () => {
  try {
    const response = await apiConfig.get('/api/trust-center/dashboard/stats');
    return response.data;
  } catch (error) {
    console.error("Failed to load TrustCenter dashboard stats:", error);
    throw error;
  }
};


export const fetchPrivacyDashboardApi = async () => {
  try {
    const response = await apiConfig.get('/api/privacy/dashboard');
    return { data: response.data, isMock: false };
  } catch (error) {
    console.warn("Privacy dashboard API unavailable — using mock data");

    // ---- MOCK FALLBACK ----
    return {
      isMock: true,
      data: {
        overview: {
          totalDataSubjects: 125000,
          activeConsents: 118500,
          pendingRequests: 23,
          privacyScore: 87.5,
        },
        dataProtection: {
          gdprCompliance: 92.3,
          ccpaCompliance: 89.7,
          lgpdCompliance: 78.9,
          pipedaCompliance: 85.4,
        },
        dataSubjectRights: [
          { type: 'Access Requests', count: 45, avgProcessingTime: '12 days', compliant: true },
          { type: 'Deletion Requests', count: 28, avgProcessingTime: '8 days', compliant: true },
          { type: 'Portability Requests', count: 12, avgProcessingTime: '15 days', compliant: false },
          { type: 'Rectification Requests', count: 19, avgProcessingTime: '6 days', compliant: true },
        ],
        consentManagement: {
          totalConsents: 125000,
          activeConsents: 118500,
          withdrawnConsents: 6500,
          consentRate: 94.8,
          categories: [
            { name: 'Marketing Communications', consents: 89000, percentage: 71.2 },
            { name: 'Analytics & Cookies', consents: 105000, percentage: 84.0 },
            { name: 'Third-party Sharing', consents: 45000, percentage: 36.0 },
            { name: 'Personalization', consents: 98000, percentage: 78.4 },
          ],
        },
        privacyIncidents: [
          {
            id: 'incident-001',
            type: 'Data Breach',
            severity: 'High',
            status: 'Resolved',
            affectedRecords: 1250,
            reportedTo: ['ICO', 'Data Subjects'],
            reportedDate: '2024-01-10',
            description:
              'Unauthorized access to customer database due to misconfigured access controls',
          },
          {
            id: 'incident-002',
            type: 'Consent Violation',
            severity: 'Medium',
            status: 'In Progress',
            affectedRecords: 340,
            reportedTo: ['Internal'],
            reportedDate: '2024-01-08',
            description:
              'Marketing emails sent to users who withdrew consent',
          },
        ],
      },
    };
  }
};


export const fetchFrameworkControlsApi = async (frameworkId) => {
  try {
    // Primary URL
    const primary = `/api/compliance/frameworks/${frameworkId}/controls`;

    try {
      const response = await apiConfig.get(primary);
      const data = response.data;
      return Array.isArray(data) ? data : data.controls || [];
    } catch (primaryErr) {
      // If problem is not 404, throw
      if (primaryErr?.response?.status !== 404) throw primaryErr;
    }

    // Fallback URL
    const fallback = `/api/trust-center/compliance/frameworks/${frameworkId}/controls`;

    const fallbackResp = await apiConfig.get(fallback);
    const fallbackData = fallbackResp.data;

    return Array.isArray(fallbackData)
      ? fallbackData
      : fallbackData.controls || [];
  } catch (error) {
    console.error("Failed to load framework controls:", error);
    return [];
  }
};

export const fetchCustomerRequirements = async (customerId) => {
  try {
    const response = await apiConfig.get(
      `/api/ai-crm/api/v1/customer-requirements/${customerId}`
    );

    return response.data?.data || {};
  } catch (error) {
    console.error("Error fetching customer requirements:", error);
    throw error;
  }
};

export const createCustomerControlResponse = async (payload) => {
  try {
    const response = await apiConfig.post(
      "api/trust-center/customers-control-responses/",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};


export const updateCustomerControlResponse = async (responseId, formData) => {
  try {
    const response = await apiConfig.patch(
      `/api/trust-center/customers-control-responses/${responseId}/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Update API Error:", error);
    throw error;
  }
};


export const getAssignedEmpControlsApi = async (customerFrameworkId, page = 1, pageSize = 50, search = '') => {
  console.log("api")
  const pg = pageSize === 'all' ? 1 : page;
    const res = await apiConfig.get(
    `/api/customer-framework/employee-controls/${customerFrameworkId}/?page=${pg}&page_size=${pageSize}&search=${encodeURIComponent(search)}`
  );
  return res.data;
};

export const getAssignedControls = async (customerId, frameworkId, employeeId = null) => {
  try {
    const employeeFilter = employeeId ? `&employee_id=${employeeId}` : "";
        const res = await apiConfig.get(
      `/api/trust-center/assigned-controls/${customerId}/?framework_id=${frameworkId}${employeeFilter}`
    );
    return res.data;  
  } catch (err) {
    console.error("assigned controls API error:", err);
    return { results: [] };
  }
}
export const updateControlResponse = async (responseId, payload) => {
  try {
    const res = await apiConfig.patch(
      `/api/trust-center/customers-control-responses/${responseId}/`,
      payload
    );
    return res.data;
  } catch (err) {
    console.error("❌ Error updating control response", err);
    throw err;
  }
};

export const uploadEvidence = async (responseId, formData) => {
  try {
    const res = await apiConfig.patch(
      `/api/trust-center/customers-control-responses/${responseId}/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return res.data;
  } catch (err) {
    console.error("❌ Error uploading evidence", err);
    throw err;
  }
};

/**
 * AI Compliance Gap Analysis Chatbot
 * @param {string} message - User's message
 * @param {string|null} frameworkId - Selected framework ObjectId (or null)
 * @param {Array} conversationHistory - Previous [{role, content}] turns
 */
export const complianceGapChatApi = async (message, frameworkId = null, conversationHistory = []) => {
  try {
    const response = await apiConfig.post('/api/trust-center/ai/gap-chat', {
      message,
      framework_id: frameworkId,
      conversation_history: conversationHistory,
    });
    return response.data;
  } catch (error) {
    console.error('Gap chatbot error:', error);
    throw error;
  }
};
// ── Framework Master catalog (super admin) ──────────────────────────────────

export const getFrameworkMasterListApi = async () => {
  const res = await apiConfig.get('/api/trust-center/framework-master/');
  return res.data;
};

export const getFrameworkMasterControlsApi = async (frameworkId, page = 1, pageSize = 50, search = '') => {
  const pg = pageSize === 'all' ? 1 : page;
  const res = await apiConfig.get(
    `/api/trust-center/framework-master/${frameworkId}/controls/?page=${pg}&page_size=${pageSize}&search=${encodeURIComponent(search)}`
  );
  return res.data;
};

export const getCustomerFrameworkControlsApi = async (customerFrameworkId, page = 1, pageSize = 50, search = '') => {
  const pg = pageSize === 'all' ? 1 : page;
  const res = await apiConfig.get(
    `/api/customer-framework/get-customer-controls/${customerFrameworkId}/?page=${pg}&page_size=${pageSize}&search=${encodeURIComponent(search)}`
  );
  return res.data;
};

export const getCustomerFrameworkDocumentsApi = async (controlId) => {
  const res = await apiConfig.get(
    `/api/customer-framework/get-customer-documents/${controlId}/`
  );
  return res.data;
};

export const getUserControlsApi = async ({ page = 1, pageSize = 20, search = "", frameworkId = "", category = "" } = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (search) params.set("search", search);
  if (frameworkId) params.set("framework_id", frameworkId);
  if (category) params.set("category", category);

  const res = await apiConfig.get(`/api/document-library/user-controls/?${params.toString()}`);
  return res.data;
};

// ── Framework Master CRUD ────────────────────────────────────────────────────

export const createFrameworkMasterApi = async (payload) => {
  const res = await apiConfig.post('/api/trust-center/framework-master/create/', payload);
  return res.data;
};

export const getFrameworkMasterDetailApi = async (frameworkId) => {
  const res = await apiConfig.get(`/api/trust-center/framework-master/${frameworkId}/`);
  return res.data;
};

export const updateFrameworkMasterApi = async (frameworkId, payload) => {
  const res = await apiConfig.put(`/api/trust-center/framework-master/${frameworkId}/`, payload);
  return res.data;
};

export const deleteFrameworkMasterApi = async (frameworkId) => {
  const res = await apiConfig.delete(`/api/trust-center/framework-master/${frameworkId}/`);
  return res.data;
};

export const createFrameworkMasterControlApi = async (frameworkId, payload) => {
  const res = await apiConfig.post(`/api/trust-center/framework-master/${frameworkId}/controls/create/`, payload);
  return res.data;
};

export const updateFrameworkMasterControlApi = async (controlId, payload) => {
  const res = await apiConfig.put(`/api/trust-center/framework-master/controls/${controlId}/`, payload);
  return res.data;
};
export const updateCustomerFrameworkMasterControlApi = async (payload) => {
  const res = await apiConfig.post(`/api/customer-framework/edit-customer-control/`, payload);
  return res.data;
};
export const deleteFrameworkMasterControlApi = async (controlId) => {
  const res = await apiConfig.delete(`/api/trust-center/framework-master/controls/${controlId}/`);
  return res.data;
};

/**
 * Export Framework Master and Controls to Excel
 * Downloads an Excel file with framework names and associated control IDs
 */
export const exportFrameworkMasterApi = async () => {
  try {
    // Use fetch for blob response instead of axios
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL || ''}/api/trust-center/framework-master/export/`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();

    // Create a download link and trigger it
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Framework_Controls_Export.xlsx');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Failed to export framework master:', error);
    throw error;
  }
};

/**
 * Export Document Library Master to Excel
 * Downloads an Excel file with document details and framework names
 */
export const exportDocumentLibraryMasterApi = async () => {
  try {
    // Use fetch for blob response instead of axios
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL || ''}/api/trust-center/document-library-master/export/`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();

    // Create a download link and trigger it
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Document_Library_Master_Export.xlsx');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Failed to export document library master:', error);
    throw error;
  }
};

/**
 * Import Framework Master and Control data from Excel file
 * @param {File} file - Excel file to import
 * @returns {Object} Import summary with created/reused/skipped counts
 */
export const importFrameworkMasterApi = async (file) => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL || ''}/api/trust-center/framework-master/import/`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Import failed');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Failed to import framework master:', error);
    throw error;
  }
};

// ── Risk Master picker (for control form) ────────────────────────────────────
export const getRiskMasterPickerApi = async (search = '', page = 1, pageSize = 200) => {
  const res = await apiConfig.get(
    `/api/trust-center/risk-master/picker/?search=${encodeURIComponent(search)}&page=${page}&page_size=${pageSize}`
  );
  return res.data;
};

// ── Document Library Master ──────────────────────────────────────────────────

export const getDocumentLibraryMasterFileUrlApi = async (docId) => {
  const res = await apiConfig.get(`/api/trust-center/document-library-master/${docId}/file-url/`);
  return res.data; // { url: '...' }
};
export const getCustomerDocumentFileUrlApi = async (documentId) => {
  const res = await apiConfig.get(`/api/customer-framework/document-library/${documentId}/file-url/`);
  return res.data; // { url: '...' }
};
// The file itself, streamed by our backend. Unlike the pre-signed S3 link it is
// same-origin, so the in-app viewer can show it and tell when it has been read.
export const getCustomerDocumentFileApi = async (documentId) => {
  const res = await apiConfig.get(`/api/customer-framework/document-library/${documentId}/file/`, {
    responseType: 'blob',
  });
  return res.data; // Blob with the file's content type
};
// A customized file uploaded for one control of the document (fileType 'auditor':
// a file the auditor attached to their review of it), streamed the same way.
export const getCustomizedFileBlobApi = async (documentId, controlId, fileId, fileType = 'customized') => {
  const res = await apiConfig.get(`/api/customer-framework/document-library/${documentId}/file/`, {
    params: { control_id: controlId, file_id: fileId, file_type: fileType },
    responseType: 'blob',
  });
  return res.data;
};
// A Document Library master record's file, streamed through the backend.
export const getDocumentLibraryMasterFileBlobApi = async (docId) => {
  const res = await apiConfig.get(`/api/trust-center/document-library-master/${docId}/file/`, {
    responseType: 'blob',
  });
  return res.data;
};
export const uploadDocumentLibraryMasterFileApi = async (docId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiConfig.post(
    `/api/trust-center/document-library-master/${docId}/upload-file/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data;
};

export const createDocumentLibraryMasterApi = async (payload) => {
  const res = await apiConfig.post('/api/trust-center/document-library-master/create/', payload);
  return res.data;
};

export const updateDocumentLibraryMasterApi = async (docId, payload) => {
  const res = await apiConfig.put(`/api/trust-center/document-library-master/${docId}/`, payload);
  return res.data;
};

export const deleteDocumentLibraryMasterApi = async (docId) => {
  const res = await apiConfig.delete(`/api/trust-center/document-library-master/${docId}/delete/`);
  return res.data;
};

export const getDocumentLibraryMasterApi = async (page = 1, pageSize = 20, filters = {}) => {
  const params = new URLSearchParams({ page, page_size: pageSize });
  if (filters.search) params.set('search', filters.search);
  if (filters.framework_id) params.set('framework_id', filters.framework_id);
  if (filters.category) params.set('category', filters.category);
  const res = await apiConfig.get(`/api/trust-center/document-library-master/?${params}`);
  return res.data;
};

export const acknowledgeControlsApi = async (documentId, controlIds) => {
  const res = await apiConfig.post(
    `/api/customer-framework/document-library/${documentId}/acknowledge-controls/`,
    { control_ids: controlIds }
  );
  return res.data;
};

export const acknowledgeDocumentApi = async (documentId, controlId) => {
  try {
    const res = await apiConfig.post(`/api/customer-framework/document-library/${documentId}/acknowledge/`,{controlId: controlId});
    return res.data;
  } catch (err) {
    console.error("Error acknowledging document:", err);
    throw err;
  }
};

export const assignSelectedControlsToCustomerApi = async ({ employeeId, controlIds, dueDate }) => {
  const res = await apiConfig.post('/api/customer-framework/assign-control-to-customer/', {
    employeeId,
    controlIds,
    dueDate,
  });
  return res.data;
};

export const approveDocumentApi = async (documentId, controlId, comment, fileId) => {
  const payload = { comment, controlId };
  if (fileId) payload.fileId = fileId;
  const res = await apiConfig.post(`/api/customer-framework/documents/${documentId}/approve`, payload);
  return res.data;
};
export const rejectCustomizedFileDocumentApi = async (documentId, controlId, comment, fileId) => {
  const payload = { comment, controlId };
  if (fileId) payload.fileId = fileId;
  const res = await apiConfig.post(`/api/customer-framework/customized-file/${documentId}/reject`, payload);
  return res.data;
};

export const approveCustomizedFileDocumentApi = async (documentId, controlId, comment, fileId) => {
  const payload = { comment, controlId };
  if (fileId) payload.fileId = fileId;
  const res = await apiConfig.post(`/api/customer-framework/customized-file/${documentId}/approve`, payload);
  return res.data;
};
export const flagDocumentApi = async(documentId, controlId, comment) => {
  const res = await apiConfig.post(`/api/customer-framework/documents/${documentId}/flag`, { comment, controlId });
  return res.data;
};

// Comment-only Submit for an already-Accepted/Flagged control - persists an
// Auditor comment WITHOUT changing the existing Approve/Flag decision.
export const addAuditorCommentApi = async (documentId, controlId, comment) => {
  const res = await apiConfig.post(`/api/customer-framework/documents/${documentId}/comment`, { comment, controlId });
  return res.data;
};

/**
 * Customer Admin: create/edit a document in their OWN customer's Document
 * Library and link it to their own controls. Mirrors the Platform Admin
 * document-library-master create/update/upload-file workflow, but scoped to
 * the caller's own customer (server-derived) - never the global catalog.
 */
export const createCustomerDocumentApi = async (payload) => {
  const res = await apiConfig.post(`/api/customer-framework/customer-documents/create/`, payload);
  return res.data;
};

export const updateCustomerDocumentApi = async (docId, payload) => {
  const res = await apiConfig.put(`/api/customer-framework/customer-documents/${docId}/update/`, payload);
  return res.data;
};

export const uploadCustomerDocumentFileApi = async (docId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiConfig.post(
    `/api/customer-framework/customer-documents/${docId}/upload-file/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data;
};

export const uploadFlaggedDocumentFileApi = async (payload) => {
  const response = await apiConfig.post(`/api/customer-framework/flagged/upload-file/`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const addCommentToFlaggedApi = async (payload) => {
  const response = await apiConfig.post(`api/customer-framework/flagged/user/comment/`,payload);
  return response.data;
};
export const getFlaggedFileUrlApi = async (flaggedId, fileId) => {
  const response = await apiConfig.get(
    `api/customer-framework/flagged/get-file/`,
    {
      params: {
        auditorResId: flaggedId,
        fileId: fileId,
      },
    }
  );
  return response.data;
};

